import { Badge, Chip, Dialog, DialogContent, DialogTitle, Grid, IconButton, List, ListItem, ListItemText, Tooltip } from "@material-ui/core";
import { makeStyles, Theme, withStyles } from '@material-ui/core/styles';
import Close from '@material-ui/icons/Close';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import QuestionAnswerIcon from '@material-ui/icons/QuestionAnswer';
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';
import { GridColumnProps, LinkColumn, SelectField, TextField } from "@roc/ui"
import { observer } from "mobx-react";
import { GridStore, InfiniteScrollGrid } from "@roc/feature-app-core";
import { ConversationsDialog, useCommunicationRoutesConfig, useLoanCommunicationStore } from "@roc/feature-communication";
import { TaskStatusDB, parseTaskStatusDBToStatus } from "@roc/feature-general-tasks";
import { debounce } from '@roc/feature-utils';
import { forwardRef, useImperativeHandle, useEffect, useMemo, useRef, useState } from 'react';

const REFRESH_INTERVAL_MS = 60 * 1000; // 1 minute refresh

const useStyles = makeStyles(() => ({
  container: {
    minHeight: '750px',
    '& .ag-header-cell, & .ag-header-group-cell': {
      paddingLeft: '10px',
      paddingRight: '10px',
    },
    '& .ag-cell': {
      paddingLeft: '10px',
      paddingRight: '10px',
    },
    '& .ag-header-wrap .ag-header-cell-label': {
      display: 'grid',
      textAlign: 'center',
      whiteSpace: 'normal',
      lineHeight: '1.2em',
    },
    '& .ag-header-wrap .ag-header-cell-text': {
      whiteSpace: 'normal',
      overflow: 'visible',
      textOverflow: 'clip',
      wordBreak: 'break-word',
    },
    '& .badge-overflow-cell': {
      overflow: 'visible !important',
    },
    '& .badge-overflow-cell .ag-cell-value, & .badge-overflow-cell .ag-cell-wrapper': {
      overflow: 'visible !important',
    },
    '& .ag-row.row-loan-approved:not(.ag-row-hover)': {
      backgroundColor: '#e3f2fd',
    },
    '& .ag-row.row-no-closing-date:not(.ag-row-hover)': {
      backgroundColor: '#fdecea !important',
    },
    '& .ag-row-hover': {
      backgroundColor: '#2196f34d !important',
    },
  },
}))

const useFloatingFilterStyles = makeStyles((theme: Theme) => ({
  root: {
    width: '100%',
    '& .MuiOutlinedInput-input, & .MuiSelect-select': {
      fontSize: 12,
      padding: '4px 6px',
    },
    '& .MuiFormHelperText-root': {
      display: 'none',
    },
  },
}))

const rowClassRules = {
  'row-loan-approved': (params: any) => params.data?.loanApproved?.status === TaskStatusDB.ACCEPTED,
  'row-no-closing-date': (params: any) => !params.data?.expectedClosingDate,
};

const getFilterConfig = (column: any) => {
  const filterConfigByRenderer = {
    taskStatusCellRenderer: {
      type: 'select',
      options: [
        { label: 'All', value: '' },
        ...Object.values(TaskStatusDB).map(status => ({
          label: parseTaskStatusDBToStatus(status),
          value: status,
        })),
      ],
    },
    wireRequestedCellRenderer: {
      type: 'select',
      options: [
        { label: 'All', value: '' },
        { label: 'Wire Due', value: 'Wire Due' },
        { label: 'Yes', value: 'Yes' },
        { label: 'No', value: 'No' },
      ],
    },
    closingConfirmCellRenderer: {
      type: 'select',
      options: [
        { label: 'All', value: '' },
        { label: 'Yes', value: 'Yes' },
        { label: 'No', value: 'No' },
      ],
    },
    exceptionsAndClosingConditionsCellRender: {
      type: 'select',
      options: [
        { label: 'All', value: '' },
        { label: 'Yes', value: 'YES' },
        { label: 'No', value: 'NO' },
      ],
    },
  };

  return filterConfigByRenderer[column.cellRenderer] ?? { type: 'text' };
};

const gridColumnFilter = forwardRef((props: any, ref) => {
  useImperativeHandle(ref, () => ({
    doesFilterPass: () => true,
    isFilterActive: () => false,
    getModel: () => null,
    setModel: (model: any) => props.onValueChange?.(model?.value ?? ''),
  }));

  return null;
});

const gridColumnFloatingFilter = forwardRef((props: any, ref) => {
  const classes = useFloatingFilterStyles();
  const [value, setValue] = useState('');
  const config = props.config ?? { type: 'text' };

  useImperativeHandle(ref, () => ({
    onParentModelChanged: (parentModel: any) => setValue(parentModel?.value ?? ''),
  }));

  const handleChange = (newValue: any) => {
    setValue(newValue);
    props.parentFilterInstance((instance: any) => instance.setModel({ value: newValue }));
  };

  if (config.type === 'select') {
    return (
      <SelectField
        testId={`filter-${props.column?.getColId?.()}`}
        className={classes.root}
        fullWidth
        variant="outlined"
        value={value}
        options={config.options}
        onChange={handleChange}
        style={{ backgroundColor: '#fff' }}
      />
    );
  }

  return (
    <TextField
      testId={`filter-${props.column?.getColId?.()}`}
      className={classes.root}
      fullWidth
      variant="outlined"
      size="small"
      value={value}
      onChange={e => handleChange(e.target.value)}
      style={{ backgroundColor: '#fff' }}
    />
  );
});

const StyledBadge = withStyles(theme => ({
  badge: {
    backgroundColor: '#7fadea',
    border: `2px solid ${theme.palette.background.paper}`,
    color: 'white',
    fontSize: '11px',
    fontWeight: 700,
    padding: '0 4px',
    right: -10,
    top: 5,
    userSelect: 'none',
  },
}))(Badge);

const getUnreadMessagesCount = unreadMessages => {
  if (unreadMessages) {
    return unreadMessages > 99 ? '99+' : unreadMessages;
  }
  return undefined;
};

const getDisplayStatus = (value?: string | null) => {
  if (!value) return '';

  return value
    .toLowerCase()
    .split('_')
    .filter(Boolean)
    .map(word => word[0].toUpperCase() + word.slice(1))
    .join('_');
};

const frameworkComponents = {
  gridColumnFilter,
  gridColumnFloatingFilter,
  loanIdCellRenderer: params => {
    const loanId = params.value;
    const [showConversations, setShowConversations] = useState(false);
    const { loanCommunicationStore } = useLoanCommunicationStore();
    const { communicationRoutesConfig } = useCommunicationRoutesConfig();

    const handleShowConversations = () => {
      setShowConversations(true);
      params.onConversationsOpenChange?.(true);
    };

    const handleCloseConversations = () => {
      setShowConversations(false);
      params.onConversationsOpenChange?.(false);
    };

    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <StyledBadge badgeContent={getUnreadMessagesCount(params.node.data?.unreadMessages)}>
          <LinkColumn
            value={loanId}
            url={'#'}
            onClick={e => {
              open('/nportal/#/loan_information/' + loanId);
              e.preventDefault();
            }}
          />
        </StyledBadge>
        <Tooltip title="See All Messages">
          <IconButton
            size="small"
            style={{ padding: 0 }}
            onClick={handleShowConversations}
          >
            <QuestionAnswerIcon style={{ fontSize: 18, color: '#7fadea' }} />
          </IconButton>
        </Tooltip>
        {showConversations && (
          <ConversationsDialog
            open={showConversations}
            loanId={Number(loanId)}
            shouldFetch={true}
            onClose={handleCloseConversations}
            communicationStore={loanCommunicationStore}
            routePath={communicationRoutesConfig.conversations(`${loanId}`).children.conversation().path}
          />
        )}
      </div>
    )
  },
  taskStatusCellRenderer: ({ value }) => {
    const getStatusColor = (status) => {
      if (status === TaskStatusDB.ACCEPTED) return 'rgba(72, 173, 37, 1)';
      if (status === TaskStatusDB.PENDING || status === TaskStatusDB.MORE_INFO_NEEDED) return 'rgba(217, 68, 82, 1)';
      if (status === TaskStatusDB.BEING_REVIEWED) return 'rgba(221, 167, 0, 1)';

      return '#999';
    }

    const formatLastUploadDate = (dateParts?: number[]) => {
      if (!dateParts || dateParts.length < 5) return '';

      const [year, month, day, hour, minute] = dateParts;

      return new Date(year, month - 1, day, hour, minute).toLocaleString(undefined,
        { year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit', }
      );
    };

    const status = value?.status;
    const assignee = status === TaskStatusDB.BEING_REVIEWED ? value?.assignee : '';
    const lastUploadDate = status === TaskStatusDB.BEING_REVIEWED ? formatLastUploadDate(value?.lastUploadDate) : '';
    const childTaskNames = Array.isArray(value?.childTaskNames) ? value.childTaskNames.filter(Boolean) : [];
    const statusLine =
      `${parseTaskStatusDBToStatus(status)}` +
      `${assignee ? ` by ${assignee}` : ''}` +
      `${lastUploadDate ? `. Last Upload Date: ${lastUploadDate}` : ''}`;
    const tooltipTitle = childTaskNames.length === 0 ? statusLine : (
      <div>
        <div>{statusLine}</div>
        <ul style={{ fontSize: 12, margin: '4px 0 0', paddingLeft: 16 }}>
          {childTaskNames.map((name) => (
            <li key={name}>{name}</li>
          ))}
        </ul>
      </div>
    );

    return (
      <Tooltip title={tooltipTitle}>
        <FiberManualRecordIcon style={{ display: 'flex', color: getStatusColor(status) }} />
      </Tooltip>
    );
  },
  wireExceptionsCellRenderer: ({ value }) => {
    const [isModalOpen, setIsModalOpen] = useState(false);

    const wireExceptions = (value || '')
      .split(';')
      .map(exception => exception.trim())
      .filter(Boolean);

    if (wireExceptions.length === 0) {
      return null;
    }

    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Badge badgeContent={wireExceptions.length} color="error" overlap="rectangle">
          <IconButton size="small" onClick={() => setIsModalOpen(true)} style={{ padding: 0 }}>
            <ErrorOutlineIcon style={{ color: '#7fadea' }} />
          </IconButton>
        </Badge>
        <Dialog open={isModalOpen} onClose={() => setIsModalOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>
            Wire Exceptions
            <IconButton
              aria-label="close"
              onClick={() => setIsModalOpen(false)}
              style={{ position: 'absolute', right: 8, top: 8 }}
            >
              <Close />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <List>
              {wireExceptions.map((exception, index) => (
                <ListItem key={index} divider={index < wireExceptions.length - 1}>
                  <ListItemText primary={exception} />
                </ListItem>
              ))}
            </List>
          </DialogContent>
        </Dialog>
      </div>
    );
  },
  wireRequestedCellRenderer: ({ value }) => {
    if (!value) {
      return null;
    }

    return (<Chip
      size="small"
      label={getDisplayStatus(value)}
      style={{
        backgroundColor: (!value || value === "No") ?  '#d6828a' : '#89C053',
        color: 'white',
        fontWeight: 700,
      }}
    />)
  },
  closingConfirmCellRenderer: ({ value }: any) => {
    const displayValue: string = value === 'Yes' || value === true ? 'Yes' : 'No';
    return (
      <Chip
        size="small"
        label={displayValue}
        style={{
          backgroundColor: displayValue === 'No' ? '#d6828a' : '#89C053',
          color: 'white',
          fontWeight: 700,
        }}
      />
    );
  },
  exceptionsAndClosingConditionsCellRender: ({ value }) => {
    if (!value) {
      return (
        <Chip
          size="small"
          label="No"
          style={{
            backgroundColor: '#bebebe',
            color: 'white',
            fontWeight: 700,
          }}
        />
      );
    }
    const conditions = value.split(';').map(item => item.trim()).filter(Boolean);
    return (
      <Tooltip
        title={
          <ul style={{ fontSize: 12, margin: 0, paddingLeft: '16px' }}>
            {conditions.map((item, index) => (
              <li key={index}>{item}</li>
            ))}
          </ul>
        }
      >
        <Chip
          size="small"
          label="Yes"
          style={{
            backgroundColor: '#dd0000',
            color: 'white',
            fontWeight: 700,
          }}
        />
      </Tooltip>
    );
  }
};

interface ClosingAgreedLoansGridV2Interface {
  columns: GridColumnProps[];
  store: GridStore;
}

export const ClosingAgreedLoansGridV2 = observer(({ columns, store }: ClosingAgreedLoansGridV2Interface) => {
  const classes = useStyles();
  const [gridApi, setGridApi] = useState(null);
  const [hasOpenConversation, setHasOpenConversation] = useState(false);
  const filtersRef = useRef<Record<string, any>>({});
  const openConversationsCountRef = useRef(0);

  useEffect(() => {
    if (!gridApi || hasOpenConversation) return;

    const intervalId = setInterval(() => gridApi.purgeInfiniteCache(), REFRESH_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [gridApi, hasOpenConversation]);

  const handleConversationsOpenChange = (isOpen: boolean) => {
    openConversationsCountRef.current += isOpen ? 1 : -1;
    setHasOpenConversation(openConversationsCountRef.current > 0);
  };

  const handleGridReady = (params: any) => {
    setGridApi(params.api);
  };

  const applyFilters = async (values: Record<string, any>) => {
    const filterParams = {};
    const dropdownFilters = {};

    columns.forEach((column: any) => {
      const value = values[column.field];
      if (value === undefined || value === '') return;

      if (getFilterConfig(column).type === 'select') {
        dropdownFilters[column.field] = value;
      } else {
        filterParams[column.field] = value;
      }
    });

    await store.setFiltersParam({ filters: filterParams, dropdownFilters }, true);
    gridApi?.purgeInfiniteCache();
  };

  const debouncedApplyFilters = useMemo(() => debounce(applyFilters), [store, gridApi]);

  const handleFilterChange = (column: any, value: any) => {
    filtersRef.current = { ...filtersRef.current, [column.field]: value };

    if (getFilterConfig(column).type === 'select') {
      applyFilters(filtersRef.current);
    } else {
      debouncedApplyFilters(filtersRef.current);
    }
  };

  const columnsWithFilterRow = useMemo(() => columns.map((column: any) => ({
    ...column,
    suppressMovable: true,
    filter: 'gridColumnFilter',
    filterParams: { onValueChange: (value: any) => handleFilterChange(column, value) },
    floatingFilter: true,
    floatingFilterComponent: 'gridColumnFloatingFilter',
    floatingFilterComponentParams: { config: getFilterConfig(column), suppressFilterButton: true },
    ...(column.cellRenderer === 'loanIdCellRenderer' && {
      cellRendererParams: { onConversationsOpenChange: handleConversationsOpenChange },
    }),
  })), [columns, store, gridApi]);

  return (
    <Grid container direction="column" wrap="nowrap" className={classes.container}>
      <InfiniteScrollGrid
        columns={columnsWithFilterRow}
        frameworkComponents={frameworkComponents}
        store={store}
        rowHeight={80}
        headerHeight={100}
        rowClassRules={rowClassRules}
        gridReady={handleGridReady}
      />
    </Grid>
  )
})
